<template>
  <div class="soldera-inventory-content container-fluid p-4 pt-11 pb-12">
    <div class="d-flex flex-column align-items-center mb-4">
      <h2 class="mb-1">{{ $t("soldera_inventory.title") }}</h2>
      <p class="text-body-tertiary mb-0">
        {{ $t("soldera_inventory.description") }}
      </p>
    </div>

    <div
      v-if="accountLoaded && !isEnabled"
      class="alert alert-info"
      data-testid="not-enabled"
    >
      {{
        $t("soldera_inventory.not_enabled", { email: SOLDERA_SUPPORT_EMAIL })
      }}
    </div>

    <template v-else-if="accountLoaded">
      <ul class="nav nav-underline mb-4" data-testid="inventory-tabs">
        <li v-for="tab in TABS" :key="tab.name" class="nav-item">
          <router-link
            :to="{ name: tab.name }"
            class="nav-link"
            active-class="active"
            :data-testid="`tab-${tab.key}`"
          >
            {{ $t(`soldera_inventory.tabs.${tab.key}`) }}
          </router-link>
        </li>
      </ul>
      <router-view />
    </template>
  </div>
</template>

<script setup lang="ts">
import { useAccount } from "@/composables/account/useAccount";
import { useAccountFeatureFlags } from "@/composables/useAccountFeatureFlags";
import { SOLDERA_SUPPORT_EMAIL } from "@/constants";

import { provideOfferTerms } from "./common/useOfferTerms";

/**
 * The Soldera Inventory page: one shell, access gate and tab row around its subpages (Spot, Forward),
 * which share the offer terms it provides (`provideOfferTerms`). A new kind of inventory is a new tab and
 * child route here.
 */
const TABS = [
  { key: "spot", name: "SolderaInventorySpot" },
  { key: "forward", name: "SolderaInventoryForward" },
] as const;

const terms = provideOfferTerms();
const { accountData, loaded: accountLoaded } = useAccount((account) =>
  terms.seedAccount(account.email)
);
const { hasFlag } = useAccountFeatureFlags(accountData);
const isEnabled = hasFlag("realtime_soldera_inventory");
</script>

<style lang="scss" scoped>
// Same shell as the Market Insights and Ecolabels pages.
.soldera-inventory-content {
  background-color: var(--phoenix-body-bg);
  border-radius: 1rem 1rem 0 0;
  overflow-y: auto;
  height: 100%;
  min-height: 0;
  overscroll-behavior: contain;
}
</style>
